import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { MutationCache, QueryClient } from '@tanstack/react-query'
import { removeOldestQuery } from '@tanstack/react-query-persist-client'

import * as Sentry from '@sentry/react'

import { smartRetry, successStaleTime } from '@/shared/api/utils/query'
import { time } from '@/shared/datetime/utils/time'

import { appVersion, isDEV } from './constants'

import type { PersistQueryClientOptions } from '@tanstack/react-query-persist-client'

const mutationCache = new MutationCache({
	onSettled: async (_data, _error, _variables, _context, mutation) => {
		// Using `meta` keyword on the mutation allows us to trigger invalidation automatically
		const invalidates = mutation.meta?.invalidates
		if (!invalidates?.length) return

		// For each entry in the `meta.invalidates` array, invalidate the query key
		const invalidations = invalidates.map((queryKey) =>
			queryClient.invalidateQueries({ queryKey }),
		)

		// If the mutation has `meta.awaitInvalidation` set to true, await the invalidation of the query keys
		if (mutation.meta?.awaitInvalidation) {
			await Promise.all(invalidations)
		}
	},
})

/**
 * React Query Default Options
 * This will set the default stale time for all queries to 5 minutes
 * This can be overridden on a per-query basis
 */
export const queryClient = new QueryClient({
	mutationCache,
	defaultOptions: {
		queries: {
			staleTime: ({ state }) => successStaleTime(state, 5, 'm'),
			gcTime: time(5, 'm'),
			retry: smartRetry,
			// Enable error boundaries for queries
			// This will make React Query throw errors to be caught by error boundaries
			throwOnError: true,
		},
		mutations: {
			// Never retry mutations - they should be explicit user actions
			retry: false,
			// Do not throw on mutations by default since we usually use onError handlers
			// Individual mutations can still enable this if desired
			throwOnError: false,
		},
	},
})

/**
 * Persist Query Client
 * This will persist the query client to the browser's local storage, allowing
 * the user to continue from where they left off after a page reload
 *
 * @see https://tanstack.com/query/latest/docs/framework/react/plugins/persistQueryClient
 */
export const persister = createAsyncStoragePersister({
	storage: window.localStorage,
	key: '@bitlyft/query-cache',
	retry: removeOldestQuery,
	throttleTime: time(10, 's'),
	serialize: (data) =>
		JSON.stringify({
			version: appVersion,
			data,
		}),
	deserialize: (cachedString) => {
		const cached = JSON.parse(cachedString)

		if (cached.version !== appVersion || isDEV) {
			return undefined
		}

		return cached.data
	},
})

export const persistOptions: PersistQueryClientOptions = {
	persister,
	queryClient,
	buster: appVersion,
	dehydrateOptions: {
		shouldRedactErrors: (error) => {
			// Cancelled errors are treated as String
			const err = error instanceof Error ? error.message : String(error)

			Sentry.addBreadcrumb({
				category: 'react-query',
				message: `Redacting cancelled error: ${err}`,
				level: 'debug',
				data: {
					error,
				},
			})

			// We can ignore CancelledErrors
			return !err.includes('CancelledError')
		},
		// Queries that should not be persisted
		shouldDehydrateQuery: (query) => {
			// When we want a query to not be cached, we include "no-cache" in the query key
			const hasNoCacheKey = query.queryKey.includes('no-cache')
			// We want only successful queries to be persisted
			const isInvalidState = query.state.status !== 'success'

			// Don't persist if the query key has no-cache key or if the query is pending or errored
			return !hasNoCacheKey && !isInvalidState
		},
	},
	maxAge: time(1, 'd'),
}

export const clearQueryCache = async () => {
	// Cancel in-flight fetches except tenant-agnostic queries (driven by /me).
	// Queries opt in via `meta: { tenantAgnostic: true }` on their queryOptions.
	await queryClient.cancelQueries(
		{
			predicate: (query) => !query.meta?.tenantAgnostic,
			fetchStatus: 'fetching',
		},
		{ silent: true },
	)

	// Clear persisted cache from localStorage
	await persister.removeClient()

	// Invalidate all queries (mark stale → refetch on next access)
	// Old tenant data stays as stale cache until gcTime expires
	await queryClient.invalidateQueries()
}
